"use client"
import { Icon } from "@/components/formatters/MXPIcon"
import { renderFocusedOrRegularImage } from "../../utils/renderFocusedOrRegularImage"

import { FocalPointImageProps } from "../../component/FocusedImage/FocusedImageMethods"
import { RegularImageProps } from "../../utils/types/RegularImageProps"

export interface ProductFamilyItem {
  productName: string
  productLink: { url: string; destination: string }
  productImage: FocalPointImageProps | RegularImageProps | null
  productDescription: string
}

export interface ProductFamilyItemProps {
  index: number
  item: ProductFamilyItem
  ProductFamilyItems?: ProductFamilyItem[]
  Link?: any
  componentImagePriority?: "high" | "auto" | "low"
  isLCP?: boolean
}

const ITEM =
  "text-gray-m9 text-xl font-bold leading-relaxed relative block rounded-sm no-underline shadow-md border-b-10 border-yellow-m10 w-full min-h-[282px] min-sm:min-h-[350px] min-lg:w-auto min-lg:min-h-[300px] min-xl:min-h-[407px] hover:!no-underline active:!no-underline focus:!no-underline [&_*]:pointer-events-none"
const ITEM_IMG =
  "rounded-sm absolute top-0 left-0 w-full h-full bg-cover object-cover bg-center z-0"
const ITEM_DETAILS =
  "w-full h-full bg-gradient-to-b from-gray-m10/0 to-gray-m10/90 flex items-end p-4 z-1 text-white absolute"
const ITEM_HEADER =
  "text-lg leading-relaxed text-white max-w-[135px] min-sm:text-base min-sm:max-w-none min-lg:text-lg"
const ITEM_DESC =
  "text-gray-m9 font-normal block px-2.5 hover:no-underline t2-dark:text-white text-base h-[102px] min-sm:text-base min-sm:leading-6 min-sm:mt-[22px] min-sm:h-auto min-lg:text-lg min-lg:leading-8 min-lg:mt-7"

export const ProductFamilyItem = ({
  index,
  item,
  Link,
  componentImagePriority,
  isLCP = false,
}: ProductFamilyItemProps) => {
  const productFamilyItemSizes = {
    desktop: {
      width: 351,
      height: 393,
      ratio: {
        width: 351,
        height: 393,
      },
    },
    tablet: {
      width: 351,
      height: 393,
      ratio: {
        width: 351,
        height: 393,
      },
    },
    mobile: {
      width: 351,
      height: 393,
      ratio: {
        width: 351,
        height: 393,
      },
    },
  }

  const isFocalPointImage = (
    item: FocalPointImageProps | RegularImageProps | null,
  ) => {
    if (!item) return false
    return "focalPointImage" in item
  }

  const mediaClasses = isFocalPointImage(item?.productImage)
    ? "absolute inset-0"
    : ""

  const itemContent = (
    <>
      <div className={mediaClasses}>
        {renderFocusedOrRegularImage(
          item?.productImage,
          productFamilyItemSizes,
          ITEM_IMG,
          90,
          componentImagePriority,
          isLCP,
        )}
      </div>
      <div className={ITEM_DETAILS}>
        <div className="flex items-center justify-between w-full">
          <h3 className={ITEM_HEADER}>{item?.productName}</h3>
          <Icon
            className="p-3 border border-white rounded-full"
            type="t2u-chevron-right"
            size={48}
          />
        </div>
      </div>
      <div className={ITEM_DESC}>{item?.productDescription}</div>
    </>
  )

  if (Link) {
    return (
      <Link
        to={item?.productLink?.url || item?.productLink?.destination}
        key={index}
        tabIndex={0}
        className={ITEM}
      >
        {itemContent}
      </Link>
    )
  }

  return (
    <a
      href={item?.productLink?.url || item?.productLink?.destination}
      key={index}
      tabIndex={0}
      className={ITEM}
    >
      {itemContent}
    </a>
  )
}
